import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function ExamPage() {
  useEffect(() => {
    document.title = '廿日市の塾 高校受験対策｜学習塾NICK｜廿日市市中学生の志望校合格・内申点アップ';

    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', '廿日市の塾・学習塾NICKの高校受験対策ページ。廿日市市の中学生の志望校合格・内申点アップを元公立高校教員が全力サポート。広島県高校入試対策・5教科対策・面接作文対策まで徹底指導。無料体験受付中。');
    setMeta('keywords', '廿日市 高校受験,廿日市 中学生 高校受験対策,廿日市 内申点,廿日市 志望校合格,廿日市 塾 受験,学習塾NICK 高校受験');
    setMeta('og:title', '廿日市の塾 高校受験対策｜学習塾NICK', true);
    setMeta('og:description', '廿日市市の中学生の高校受験対策は学習塾NICKへ。元公立高校教員による志望校合格・内申点アップ指導。広島県入試を知り尽くした講師が徹底サポート。', true);
    setMeta('og:url', `${import.meta.env.VITE_SITE_URL}/exam`, true);
    setMeta('twitter:title', '廿日市の塾 高校受験対策｜学習塾NICK');
    setMeta('twitter:description', '廿日市市の中学生の高校受験対策は学習塾NICKへ。元公立高校教員による志望校合格・内申点アップ指導。');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/exam`);

    // Schema.org JSON-LD
    const schemaId = 'schema-exam-page';
    let schemaEl = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!schemaEl) {
      schemaEl = document.createElement('script');
      schemaEl.id = schemaId;
      schemaEl.type = 'application/ld+json';
      document.head.appendChild(schemaEl);
    }
    schemaEl.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "廿日市の塾 高校受験対策｜学習塾NICK",
      "description": "廿日市市の中学生の高校受験対策は学習塾NICKへ。元公立高校教員による志望校合格・内申点アップ指導。広島県入試を知り尽くした講師が徹底サポート。",
      "url": `${import.meta.env.VITE_SITE_URL}/exam`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "ホーム", "item": `${import.meta.env.VITE_SITE_URL}/` },
          { "@type": "ListItem", "position": 2, "name": "高校受験対策", "item": `${import.meta.env.VITE_SITE_URL}/exam` }
        ]
      },
      "mainEntity": {
        "@type": "Course",
        "name": "廿日市 高校受験対策コース",
        "description": "廿日市市の中学生向け高校受験対策。内申点対策・5教科対策・面接作文対策まで元公立高校教員が徹底指導。志望校合格率100%（過去3年間）。",
        "provider": {
          "@type": "Organization",
          "name": "学習塾NICK",
          "url": `${import.meta.env.VITE_SITE_URL}/`
        },
        "educationalLevel": "中学生",
        "teaches": ["高校受験対策", "内申点アップ", "5教科対策", "面接対策", "作文対策"],
        "hasCourseInstance": {
          "@type": "CourseInstance",
          "courseMode": "onsite",
          "location": {
            "@type": "Place",
            "name": "学習塾NICK",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "大野中央四丁目４－１６",
              "addressLocality": "廿日市市",
              "addressRegion": "広島県",
              "postalCode": "739-0400",
              "addressCountry": "JP"
            }
          }
        }
      }
    });

    return () => {
      document.title = '廿日市の塾なら学習塾NICK｜廿日市市の中学生塾・成績アップ・高校受験対策';
      if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/`);
      const s = document.getElementById(schemaId);
      if (s) s.remove();
    };
  }, []);

  const strategies = [
    {
      title: '内申点と入試点の両方を重視',
      description: '広島県の高校入試では、内申点と入試当日の点数の両方が合否を左右します。中1から中3の1学期までの内申点が評価対象となるため、日々の定期テスト対策と提出物管理が重要です。学習塾NICKでは、内申点対策と入試対策を並行して進め、総合的な受験力を養成します。',
      icon: 'ri-scales-3-line'
    },
    {
      title: '志望校に合わせた学習計画',
      description: '志望校のレベルや出題傾向に合わせて、最適な学習計画を立案します。公立高校で10年間進路指導を担当してきた経験を活かし、各高校の特徴や求められる学力を熟知。一人ひとりの現状と目標に応じた、無理のない計画で合格を目指します。',
      icon: 'ri-roadmap-line'
    },
    {
      title: '5教科バランスの取れた学習',
      description: '高校受験では5教科すべてが評価されます。得意科目を伸ばしつつ、苦手科目を克服することが合格の鍵。学習塾NICKでは、各科目の特性に合わせた指導で、バランスの取れた学力を養成します。',
      icon: 'ri-book-open-line'
    },
    {
      title: '過去問演習と弱点克服',
      description: '広島県の公立高校入試の過去問を徹底的に分析。出題傾向を把握し、頻出問題を重点的に演習します。模擬試験の結果から弱点を洗い出し、個別に対策を行うことで、確実な得点力を身につけます。',
      icon: 'ri-file-list-3-line'
    }
  ];

  const timeline = [
    {
      period: '中3 春〜夏',
      title: '基礎固めと苦手克服',
      tasks: [
        '中1・中2の総復習',
        '苦手単元の徹底克服',
        '定期テスト対策（内申点確保）',
        '学習習慣の確立'
      ],
      focus: 'この時期は基礎学力の定着が最優先。夏休みを利用して、中1・中2の内容を総復習し、苦手分野を克服します。'
    },
    {
      period: '中3 夏休み',
      title: '集中特訓と実力養成',
      tasks: [
        '夏期講習での集中学習',
        '1・2年の総まとめ',
        '応用問題への挑戦',
        '学習時間の大幅確保'
      ],
      focus: '受験の天王山。1日6〜8時間の学習時間を確保し、基礎から応用まで幅広く学習。この時期の頑張りが合否を分けます。'
    },
    {
      period: '中3 秋',
      title: '実践力の養成',
      tasks: [
        '過去問演習の開始',
        '模擬試験での実力確認',
        '志望校の絞り込み',
        '弱点分野の補強'
      ],
      focus: '過去問演習を通じて、入試問題の形式に慣れます。模擬試験で現在の実力を把握し、志望校を具体的に決定していきます。'
    },
    {
      period: '中3 冬',
      title: '総仕上げと入試直前対策',
      tasks: [
        '過去問の徹底演習',
        '予想問題での実戦練習',
        '面接・作文対策',
        '体調管理とメンタルケア'
      ],
      focus: '入試直前期。過去問を繰り返し解き、時間配分や解答テクニックを磨きます。面接や作文の対策も行い、万全の状態で本番を迎えます。'
    }
  ];

  const subjects = [
    {
      name: '数学',
      strategies: [
        '計算問題での確実な得点',
        '関数・図形の頻出パターン習得',
        '証明問題の論理的な記述力',
        '応用問題への対応力養成'
      ]
    },
    {
      name: '英語',
      strategies: [
        '文法の完全理解',
        '長文読解のスピードアップ',
        '英作文の表現力向上',
        'リスニング対策'
      ]
    },
    {
      name: '国語',
      strategies: [
        '読解問題の解法テクニック',
        '記述問題の書き方指導',
        '古文・漢文の基礎固め',
        '語彙力の強化'
      ]
    },
    {
      name: '理科',
      strategies: [
        '実験・観察問題の対策',
        '計算問題の解法パターン',
        '図表の読み取り練習',
        '重要用語の確実な暗記'
      ]
    },
    {
      name: '社会',
      strategies: [
        '地理・歴史・公民の体系的理解',
        '資料読み取り問題の対策',
        '記述問題の論述力養成',
        '時事問題への対応'
      ]
    }
  ];

  const interviewPrep = [
    {
      title: '面接対策',
      description: '志望動機、自己PR、中学校生活での経験など、よく聞かれる質問への回答を準備。模擬面接を繰り返し行い、自信を持って本番に臨めるようサポートします。',
      icon: 'ri-user-voice-line'
    },
    {
      title: '作文対策',
      description: '制限時間内に論理的な文章を書く練習を重ねます。テーマに沿った構成の立て方、説得力のある論述方法を指導し、高得点を狙います。',
      icon: 'ri-edit-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-32 bg-gradient-to-br from-[#4a90e2]/10 via-white to-[#4a90e2]/5">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-[#1a2332] mb-6">
              高校受験対策
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              <strong>廿日市市</strong>の学習塾NICKでは、公立高校での進路指導経験を活かした<strong>高校受験対策</strong>で、<strong>志望校合格</strong>を全力サポート。<strong>内申点対策</strong>から<strong>入試対策</strong>まで、総合的な受験指導を行います。
            </p>
          </div>
        </section>

        {/* Strategy Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              高校受験で必要な戦略
            </h2>
            <p className="text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
              広島県の<strong>高校入試</strong>を熟知した講師が、合格に必要な戦略を徹底指導
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {strategies.map((strategy, index) => (
                <div
                  key={index}
                  className="bg-[#f8f9fa] rounded-3xl p-8 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-[#4a90e2]/10 rounded-2xl flex items-center justify-center mb-6">
                    <i className={`${strategy.icon} text-3xl text-[#4a90e2]`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-4">
                    {strategy.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {strategy.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-16 bg-[#1a2332] rounded-3xl p-10 text-white">
              <h3 className="text-2xl font-bold mb-6 text-center">
                公立高校での10年の経験を活かした進路指導
              </h3>
              <p className="text-white/80 leading-relaxed text-center max-w-4xl mx-auto">
                学習塾NICKの講師は、公立高校で10年間、進路指導や入試業務に携わってきました。広島県の<strong>高校入試制度</strong>を熟知し、各高校の特徴や求められる学力を理解しています。この経験を活かし、一人ひとりに最適な受験戦略を立案。<strong>廿日市市</strong>の<strong>中学生</strong>の<strong>志望校合格</strong>を全力でサポートします。
              </p>
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-24 bg-[#f8f9fa]">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              中3の年間学習計画
            </h2>
            <p className="text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
              <strong>高校受験</strong>に向けた、時期別の学習計画と対策内容
            </p>

            <div className="space-y-8">
              {timeline.map((phase, index) => (
                <div
                  key={index}
                  className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <div className="bg-[#4a90e2] text-white px-8 py-6">
                    <h3 className="text-2xl font-bold">{phase.period}</h3>
                    <p className="text-xl mt-2">{phase.title}</p>
                  </div>

                  <div className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-bold text-[#1a2332] mb-4 flex items-center gap-3">
                          <i className="ri-task-line text-xl text-[#4a90e2]"></i>
                          主な学習内容
                        </h4>
                        <ul className="space-y-3">
                          {phase.tasks.map((task, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <i className="ri-check-line text-xl text-[#4a90e2] mt-1 flex-shrink-0"></i>
                              <span className="text-gray-700">{task}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-bold text-[#1a2332] mb-4 flex items-center gap-3">
                          <i className="ri-lightbulb-line text-xl text-[#4a90e2]"></i>
                          この時期のポイント
                        </h4>
                        <p className="text-gray-600 leading-relaxed">
                          {phase.focus}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Subjects Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              科目別の受験対策
            </h2>
            <p className="text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
              5教科それぞれの特性に合わせた、効果的な学習方法
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {subjects.map((subject, index) => (
                <div
                  key={index}
                  className="bg-[#f8f9fa] rounded-3xl p-8 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-[#4a90e2]/10 rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-book-2-line text-3xl text-[#4a90e2]"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1a2332] mb-6">
                    {subject.name}
                  </h3>
                  <ul className="space-y-3">
                    {subject.strategies.map((strategy, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <i className="ri-arrow-right-s-line text-xl text-[#4a90e2] mt-1 flex-shrink-0"></i>
                        <span className="text-gray-700">{strategy}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-16 bg-[#4a90e2]/5 rounded-3xl p-10">
              <h3 className="text-2xl font-bold text-[#1a2332] mb-6 text-center">
                過去問演習の進め方
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-bold text-[#1a2332] mb-4">
                    STEP 1：時間を計って解く
                  </h4>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    本番と同じ時間配分で過去問を解きます。時間内に解き終わる力を養成し、時間配分の感覚を身につけます。
                  </p>

                  <h4 className="font-bold text-[#1a2332] mb-4">
                    STEP 2：徹底的な解き直し
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    間違えた問題は必ず解き直し。なぜ間違えたのか、正しい解法は何かを理解するまで繰り返します。
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-[#1a2332] mb-4">
                    STEP 3：弱点の洗い出し
                  </h4>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    過去問演習の結果から、苦手分野を明確化。重点的に対策を行い、確実に得点できるようにします。
                  </p>

                  <h4 className="font-bold text-[#1a2332] mb-4">
                    STEP 4：複数年分の演習
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    過去5〜10年分の問題を解き、出題傾向を把握。頻出問題のパターンを完全にマスターします。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Interview & Essay Section */}
        <section className="py-24 bg-[#f8f9fa]">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              面接・作文対策
            </h2>
            <p className="text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
              学力試験だけでなく、面接や作文も合否を左右する重要な要素です
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {interviewPrep.map((prep, index) => (
                <div
                  key={index}
                  className="bg-white rounded-3xl p-10 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-[#4a90e2]/10 rounded-2xl flex items-center justify-center mb-6">
                    <i className={`${prep.icon} text-3xl text-[#4a90e2]`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1a2332] mb-4">
                    {prep.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {prep.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-3xl p-10">
              <h3 className="text-2xl font-bold text-[#1a2332] mb-6 text-center">
                面接でよく聞かれる質問
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  '志望動機を教えてください',
                  '中学校生活で頑張ったことは何ですか',
                  '高校に入学したら何をしたいですか',
                  '自分の長所と短所を教えてください',
                  '最近気になったニュースはありますか',
                  '将来の夢や目標を教えてください'
                ].map((question, index) => (
                  <div
                    key={index}
                    className="bg-[#f8f9fa] rounded-2xl p-6 flex items-start gap-4"
                  >
                    <i className="ri-question-line text-2xl text-[#4a90e2] mt-1 flex-shrink-0"></i>
                    <p className="text-gray-700">{question}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-4 text-center">
              合格実績
            </h2>
            <p className="text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
              <strong>廿日市市</strong>の学習塾NICKから巣立った生徒たちの合格実績
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="bg-[#f8f9fa] rounded-3xl p-10 text-center hover:shadow-xl transition-all duration-300">
                <p className="text-6xl font-bold text-[#4a90e2] mb-4">100%</p>
                <p className="text-xl font-semibold text-[#1a2332] mb-2">志望校合格率</p>
                <p className="text-sm text-gray-600">過去3年間の実績</p>
              </div>

              <div className="bg-[#f8f9fa] rounded-3xl p-10 text-center hover:shadow-xl transition-all duration-300">
                <p className="text-6xl font-bold text-[#4a90e2] mb-4">100%</p>
                <p className="text-xl font-semibold text-[#1a2332] mb-2">第一志望合格率</p>
                <p className="text-sm text-gray-600">2025年度入試</p>
              </div>

              <div className="bg-[#f8f9fa] rounded-3xl p-10 text-center hover:shadow-xl transition-all duration-300">
                <p className="text-6xl font-bold text-[#4a90e2] mb-4">50名+</p>
                <p className="text-xl font-semibold text-[#1a2332] mb-2">合格者数</p>
                <p className="text-sm text-gray-600">開校以来の累計</p>
              </div>
            </div>

            <div className="bg-[#4a90e2]/5 rounded-3xl p-10">
              <h3 className="text-2xl font-bold text-[#1a2332] mb-6 text-center">
                主な合格校（過去3年間）
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  '広島市立基町高校',
                  '広島市立舟入高校',
                  '広島県立廿日市高校',
                  '広島県立井口高校',
                  '広島県立廿日市西高校',
                  '広島県立五日市高校',
                  '広島県立国泰寺高校',
                  '私立盈進高校'
                ].map((school, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-4 text-center text-gray-700 font-medium"
                  >
                    {school}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-[#1a2332]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              志望校合格を目指すなら、学習塾NICKにお任せください
            </h2>
            <p className="text-white/80 text-lg mb-10 leading-relaxed">
              <strong>廿日市市大野</strong>の学習塾NICKでは、<strong>無料体験授業</strong>を随時受け付けております。<strong>高校受験対策</strong>の具体的な方法や、志望校合格に向けた学習計画について詳しくご説明いたします。<strong>廿日市市</strong>、<strong>宮島</strong>、<strong>阿品</strong>、<strong>大竹市</strong>など周辺地域からもお気軽にお越しください。
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/trial#trial-form-section"
                className="bg-[#4a90e2] text-white px-10 py-5 rounded-full text-lg font-semibold hover:bg-[#3a7bc8] transition-all hover:scale-105 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
              >
                無料体験に申し込む
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
              <a
                href="tel:0829-78-1638"
                className="bg-white text-[#1a2332] px-10 py-5 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all hover:scale-105 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl"></i>
                電話で問い合わせる
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
